import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, apiFetch } from '../../config/api';

function ManageFoods() {
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();
  const allergens = ['Fish', 'Shellfish', 'Soy', 'Eggs', 'Gluten', 'Dairy', 'Sesame', 'Halal', 'Pork', 'Spicy', 'Vegetarian', 'Vegan'];
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFoods();
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      if (importing) return;
      fetchFoods(false);
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [importing]);

  const fetchFoods = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      const response = await apiFetch(`${API_BASE_URL}/foods`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      let data = await response.json();
      
      // Sort by createdAt (newest first), or by name alphabetically if no createdAt
      data = data.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return new Date(b.createdAt) - new Date(a.createdAt); // newest first
        }
        return a.name.localeCompare(b.name);
      });
      
      setFoods(data || []);
    } catch (error) {
      console.error('Failed to fetch foods:', error);
      setError('Failed to load foods. Please try again.');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleGenerateLabel = (food) => {
    setSelectedFood(food);
    setShowLabelModal(true);
  };

  const handlePrintLabel = () => {
    // Create a new window for printing
    const printWindow = window.open('', '', 'height=600,width=800');
    
    // Write the label HTML to the print window
    printWindow.document.write(`
      <html>
        <head>
          <title>Allergen Label - ${selectedFood.name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .label-container { border: 4px solid black; padding: 20px; }
            .food-name { font-size: 28px; font-weight: bold; text-align: center; background: #f0f0f0; border: 2px solid black; padding: 20px; margin-bottom: 20px; }
            .warning { font-size: 12px; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; }
            td { border: 2px solid #666; padding: 15px; text-align: center; }
            .emoji { font-size: 24px; }
            .label-text { font-weight: bold; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="label-container">
            <div class="food-name">${selectedFood.name}</div>
            <p class="warning">
              Murray-Aikins Dining Hall is a tree nut/peanut free facility. This food item may contain the following allergens:
            </p>
            <table>
              <tr>
                <td class="emoji">${selectedFood.restrictions?.includes('Fish') ? '🐟' : '✗'}</td>
                <td class="label-text">Fish</td>
                <td class="emoji">${selectedFood.restrictions?.includes('Shellfish') ? '🦐' : '✗'}</td>
                <td class="label-text">Shellfish</td>
              </tr>
              <tr>
                <td class="emoji">${selectedFood.restrictions?.includes('Soy') ? '🫘' : '✗'}</td>
                <td class="label-text">Soy</td>
                <td class="emoji">${selectedFood.restrictions?.includes('Eggs') ? '🥚' : '✗'}</td>
                <td class="label-text">Eggs</td>
              </tr>
              <tr>
                <td class="emoji">${selectedFood.restrictions?.includes('Gluten') ? '🌾' : '✗'}</td>
                <td class="label-text">Gluten</td>
                <td class="emoji">${selectedFood.restrictions?.includes('Dairy') ? '🥛' : '✗'}</td>
                <td class="label-text">Dairy</td>
              </tr>
              <tr>
                <td class="emoji">${selectedFood.restrictions?.includes('Sesame') ? '🌰' : '✗'}</td>
                <td class="label-text">Sesame</td>
                <td class="emoji">${selectedFood.restrictions?.includes('Halal') ? '☪️' : '✗'}</td>
                <td class="label-text">Halal</td>
              </tr>
              <tr>
                <td class="emoji">${selectedFood.restrictions?.includes('Pork') ? '🐷' : '✗'}</td>
                <td class="label-text">Pork</td>
                <td class="emoji">${selectedFood.restrictions?.includes('Spicy') ? '🌶️' : '✗'}</td>
                <td class="label-text">Spicy</td>
              </tr>
              <tr>
                <td class="emoji">${selectedFood.restrictions?.includes('Vegetarian') ? '🥬' : '✗'}</td>
                <td class="label-text">Vegetarian</td>
                <td class="emoji">${selectedFood.restrictions?.includes('Vegan') ? '🌱' : '✗'}</td>
                <td class="label-text">Vegan</td>
              </tr>
            </table>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  const handleEdit = (food) => {
    setEditingId(food.id);
    setEditFormData({ ...food });
  };

  const handleSaveEdit = async () => {
    try {
      if (!editFormData.name?.trim()) {
        alert('Food name is required');
        return;
      }

      const response = await apiFetch(`${API_BASE_URL}/foods?id=${editingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editFormData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedFood = await response.json();
      setFoods(foods.map(f => f.id === editingId ? updatedFood : f));
      setEditingId(null);
      setEditFormData({});
      alert('Food item updated successfully!');
    } catch (error) {
      console.error('Failed to update food:', error);
      alert('Failed to update food item');
    }
  };

  const handleDelete = async (foodId) => {
    if (window.confirm('Are you sure you want to delete this food item?')) {
      try {
        const response = await apiFetch(`${API_BASE_URL}/foods?id=${foodId}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setFoods(foods.filter(f => f.id !== foodId));
        alert('Food item deleted successfully!');
      } catch (error) {
        console.error('Failed to delete food:', error);
        alert('Failed to delete food item');
      }
    }
  };

  const handleDeleteAllFoods = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL food items? This action cannot be undone.'
      ) &&
      window.confirm('This is irreversible. Are you absolutely certain?')
    ) {
      try {
        setLoading(true);
        let failureCount = 0;

        for (const food of foods) {
          try {
            const response = await apiFetch(`${API_BASE_URL}/foods?id=${food.id}`, {
              method: 'DELETE',
            });
            if (!response.ok) {
              failureCount++;
            }
          } catch (err) {
            failureCount++;
            console.error(`Failed to delete food ${food.id}:`, err);
          }
        }

        await fetchFoods();

        if (failureCount === 0) {
          alert('All food items deleted successfully!');
        } else {
          alert(`Deleted ${foods.length - failureCount} of ${foods.length} food items. ${failureCount} failed.`);
        }
      } catch (err) {
        alert('Failed to delete all food items. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRestrictionToggle = (restriction) => {
    setEditFormData(prev => {
      const restrictions = prev.restrictions?.includes(restriction)
        ? prev.restrictions.filter(r => r !== restriction)
        : [...(prev.restrictions || []), restriction];
      return { ...prev, restrictions };
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({});
  };

  // ---- Export foods to a JSON file ----
  const handleExportFoods = () => {
    try {
      const dataStr = JSON.stringify(foods, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `dhall-foods-export-${timestamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export foods.');
      console.error(err);
    }
  };

  // ---- Import foods from a JSON file ----
  const handleImportButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      const text = await file.text();
      const parsed = JSON.parse(text);
      const foodsToImport = Array.isArray(parsed) ? parsed : [parsed];

      if (foodsToImport.length === 0) {
        alert('No food items found in the selected file.');
        return;
      }

      const invalid = foodsToImport.find(f => !f || typeof f.name !== 'string' || !f.name.trim());
      if (invalid) {
        throw new Error('One or more food items in the file are missing a valid "name" field.');
      }

      let importedCount = 0;
      let updatedCount = 0;
      const failures = [];

      for (const f of foodsToImport) {
        const newFood = {
          id: f.id || undefined, // keep the original id if present
          name: f.name.trim(),
          ingredients: (f.ingredients || '').trim(),
          restrictions: Array.isArray(f.restrictions) ? f.restrictions : []
        };

        let handled = false;

        // If the file has an id, try updating that exact record first
        if (newFood.id) {
          try {
            const putResp = await apiFetch(`${API_BASE_URL}/foods?id=${newFood.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(newFood)
            });
            if (putResp.ok) {
              updatedCount += 1;
              handled = true;
            } else if (putResp.status !== 404) {
              failures.push(newFood.name);
              handled = true;
            }
            // 404 falls through to POST below
          } catch (innerErr) {
            failures.push(newFood.name);
            handled = true;
            console.error(`Failed to update food "${newFood.name}":`, innerErr);
          }
        }

        if (handled) continue;

        // Create new, preserving id if the file had one (404'd) or none at all
        try {
          const postResp = await apiFetch(`${API_BASE_URL}/foods`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newFood)
          });
          if (!postResp.ok) {
            throw new Error(`HTTP ${postResp.status}`);
          }
          importedCount += 1;
        } catch (innerErr) {
          failures.push(newFood.name);
          console.error(`Failed to import food "${newFood.name}":`, innerErr);
        }
      }

      await fetchFoods();

      const messageParts = [];
      if (importedCount > 0) messageParts.push(`Imported ${importedCount} new food item(s).`);
      if (updatedCount > 0) messageParts.push(`Updated ${updatedCount} existing item(s).`);
      if (failures.length > 0) messageParts.push(`Failed: ${failures.join(', ')}`);

      alert(messageParts.length > 0 ? messageParts.join('\n') : 'No items to import.');
    } catch (err) {
      alert(`Failed to import foods: ${err.message}`);
      console.error(err);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  // Filter foods based on search
  const filteredFoods = foods.filter(food =>
    food.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    food.ingredients.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading food items...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
          <h1 className="text-3xl font-bold text-gray-800">Manage Food Items</h1>
          <div className="flex gap-2 flex-wrap">
            <input
              type="file"
              accept="application/json,.json"
              ref={fileInputRef}
              onChange={handleImportFileSelected}
              className="hidden"
            />
            <button
              onClick={handleImportButtonClick}
              disabled={importing}
              className={`py-2 px-4 rounded-md font-semibold transition text-white ${
                importing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              {importing ? 'Importing...' : 'Import JSON'}
            </button>
            <button
              onClick={handleExportFoods}
              disabled={foods.length === 0}
              className={`py-2 px-4 rounded-md font-semibold transition text-white ${
                foods.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-slate-600 hover:bg-slate-700'
              }`}
            >
              Export JSON
            </button>
            <button
              onClick={() => navigate('/admin/food/add')}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-semibold"
            >
              + Add New Food
            </button>
            <button
              onClick={handleDeleteAllFoods}
              disabled={foods.length === 0}
              className={`py-2 px-4 rounded-md font-semibold transition text-white ${
                foods.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by name or ingredients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
            <button
              onClick={fetchFoods}
              className="ml-4 underline font-semibold hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {filteredFoods.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500 text-lg">
              {searchTerm ? 'No food items match your search.' : 'No food items found. Create one to get started!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredFoods.map(food => (
              <div key={food.id} className="bg-white rounded-lg shadow-md p-6">
                {editingId === food.id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Name</label>
                      <input
                        type="text"
                        value={editFormData.name || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Ingredients</label>
                      <textarea
                        value={editFormData.ingredients || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, ingredients: e.target.value })}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                      />
                    </div>
                    <div>
                      <span className="block text-gray-700 font-medium mb-2">Dietary Restrictions</span>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {allergens.map(restriction => (
                          <label key={restriction} className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editFormData.restrictions?.includes(restriction) || false}
                              onChange={() => handleRestrictionToggle(restriction)}
                              className="mr-2 h-4 w-4 text-blue-600 rounded"
                            />
                            <span className="text-sm text-gray-600">{restriction}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={handleSaveEdit}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-md font-semibold"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-md font-semibold"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{food.name}</h3>
                      <p className="text-gray-600 mb-3">
                        <span className="font-semibold">Ingredients:</span> {food.ingredients}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {food.restrictions?.map(restriction => (
                          <span
                            key={restriction}
                            className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                          >
                            {restriction}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleGenerateLabel(food)}
                        className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-md font-semibold text-sm"
                      >
                        🏷️ Label
                      </button>
                      <button
                        onClick={() => handleEdit(food)}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-semibold text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(food.id)}
                        className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md font-semibold text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Allergen Label Modal */}
      {showLabelModal && selectedFood && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-8 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Allergen Label</h2>
              <button
                onClick={() => setShowLabelModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Label Content */}
            <div className="border-4 border-gray-800 p-6">
              {/* Food Name - Bigger and Separate */}
              <div className="bg-gray-100 border-2 border-gray-800 p-6 mb-6 text-center">
                <h3 className="text-3xl font-bold text-gray-900">{selectedFood.name}</h3>
              </div>

              {/* Warning Text */}
              <p className="text-sm text-gray-700 mb-6">
                Murray-Aikins Dining Hall is a tree nut/peanut free facility. This food item may contain the following allergens:
              </p>

              {/* Allergen Grid */}
              <table className="w-full border-collapse">
                <tbody>
                  {/* Row 1: Fish & Shellfish */}
                  <tr>
                    <td className="border-2 border-gray-400 p-3 text-center w-1/6">
                      {selectedFood.restrictions?.includes('Fish') ? (
                        <span className="text-3xl">🐟</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center font-semibold">
                      Fish
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center w-1/6">
                      {selectedFood.restrictions?.includes('Shellfish') ? (
                        <span className="text-3xl">🦐</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center font-semibold">
                      Shellfish
                    </td>
                  </tr>

                  {/* Row 2: Soy & Eggs */}
                  <tr>
                    <td className="border-2 border-gray-400 p-3 text-center">
                      {selectedFood.restrictions?.includes('Soy') ? (
                        <span className="text-3xl">🫘</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className={`border-2 border-gray-400 p-3 text-center font-semibold ${selectedFood.restrictions?.includes('Soy') ? 'bg-yellow-50' : ''}`}>
                      Soy
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center">
                      {selectedFood.restrictions?.includes('Eggs') ? (
                        <span className="text-3xl">🥚</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className={`border-2 border-gray-400 p-3 text-center font-semibold ${selectedFood.restrictions?.includes('Eggs') ? 'bg-orange-50' : ''}`}>
                      Eggs
                    </td>
                  </tr>

                  {/* Row 3: Gluten & Dairy */}
                  <tr>
                    <td className="border-2 border-gray-400 p-3 text-center">
                      {selectedFood.restrictions?.includes('Gluten') ? (
                        <span className="text-3xl">🌾</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className={`border-2 border-gray-400 p-3 text-center font-semibold ${selectedFood.restrictions?.includes('Gluten') ? 'bg-yellow-50' : ''}`}>
                      Gluten
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center">
                      {selectedFood.restrictions?.includes('Dairy') ? (
                        <span className="text-3xl">🥛</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className={`border-2 border-gray-400 p-3 text-center font-semibold ${selectedFood.restrictions?.includes('Dairy') ? 'bg-blue-50' : ''}`}>
                      Dairy
                    </td>
                  </tr>

                  {/* Row 4: Sesame & Halal */}
                  <tr>
                    <td className="border-2 border-gray-400 p-3 text-center">
                      {selectedFood.restrictions?.includes('Sesame') ? (
                        <span className="text-3xl">🌰</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center font-semibold">
                      Sesame
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center">
                      {selectedFood.restrictions?.includes('Halal') ? (
                        <span className="text-3xl">☪️</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center font-semibold">
                      Halal
                    </td>
                  </tr>

                  {/* Row 5: Pork & Spicy */}
                  <tr>
                    <td className="border-2 border-gray-400 p-3 text-center">
                      {selectedFood.restrictions?.includes('Pork') ? (
                        <span className="text-3xl">🐷</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center font-semibold">
                      Pork
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center">
                      {selectedFood.restrictions?.includes('Spicy') ? (
                        <span className="text-3xl">🌶️</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center font-semibold">
                      Spicy
                    </td>
                  </tr>

                  {/* Row 6: Vegetarian & Vegan */}
                  <tr>
                    <td className="border-2 border-gray-400 p-3 text-center">
                      {selectedFood.restrictions?.includes('Vegetarian') ? (
                        <span className="text-3xl">🥬</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className={`border-2 border-gray-400 p-3 text-center font-semibold ${selectedFood.restrictions?.includes('Vegetarian') ? 'bg-green-100' : ''}`}>
                      Vegetarian
                    </td>
                    <td className="border-2 border-gray-400 p-3 text-center">
                      {selectedFood.restrictions?.includes('Vegan') ? (
                        <span className="text-3xl">🌱</span>
                      ) : (
                        <span className="text-3xl text-gray-300">X</span>
                      )}
                    </td>
                    <td className={`border-2 border-gray-400 p-3 text-center font-semibold ${selectedFood.restrictions?.includes('Vegan') ? 'bg-green-200' : ''}`}>
                      Vegan
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 mt-6">
              <button
                onClick={handlePrintLabel}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md font-semibold"
              >
                🖨️ Print Label
              </button>
              <button
                onClick={() => setShowLabelModal(false)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 rounded-md font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageFoods;